import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import {
  BookOpen, CheckCircle, EyeOff, Image, Play, AlertTriangle,
  Plus, FolderOpen, Download, Upload, List, AlertCircle,
  Clock, ChevronRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const TYPE_LABELS = { T: "Metin", R: "Görsel", TR: "Görselli Şık", ZTR: "Çoklu Görsel", V: "Video" };
const TYPE_COLORS = {
  T:   "bg-blue-50 text-blue-700 border-blue-200",
  R:   "bg-purple-50 text-purple-700 border-purple-200",
  TR:  "bg-cyan-50 text-cyan-700 border-cyan-200",
  ZTR: "bg-pink-50 text-pink-700 border-pink-200",
  V:   "bg-orange-50 text-orange-700 border-orange-200",
};

function StatCard({ label, value, icon: Icon, iconBg, iconColor, to, alert }) {
  const inner = (
    <div className={`bg-white border rounded-xl p-5 transition-all ${
      to ? "hover:border-blue-300 hover:shadow-sm cursor-pointer" : ""
    } ${alert ? "border-amber-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</p>
          <p className="text-3xl font-bold text-slate-900"
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {value ?? "—"}
          </p>
        </div>
        <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon size={16} className={iconColor} strokeWidth={1.5} />
        </div>
      </div>
      {to && (
        <p className="text-xs text-blue-500 mt-3 flex items-center gap-0.5">
          Listede gör <ChevronRight size={11} />
        </p>
      )}
    </div>
  );
  return to ? <Link to={to} data-testid={`card-${label}`}>{inner}</Link> : <div>{inner}</div>;
}

function AlertBanner({ count, label, to }) {
  if (!count) return null;
  return (
    <Link to={to}
      className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors"
      data-testid={`alert-${label}`}>
      <div className="flex items-center gap-2.5">
        <AlertCircle size={15} className="text-amber-500 shrink-0" />
        <span className="text-sm text-amber-800">
          <span className="font-semibold">{count} soru</span> — {label}
        </span>
      </div>
      <span className="text-xs text-amber-600 font-medium flex items-center gap-0.5">
        İncele <ChevronRight size={11} />
      </span>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/questions/meta/stats")
      .then((r) => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-20 bg-slate-100 rounded-xl animate-pulse" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const s = stats || {};
  const hasAlerts = s.missing_correct_answer > 0 || s.missing_display_type > 0;

  const summaryCards = [
    { label: "Toplam",       value: s.total,                 icon: BookOpen,      iconBg: "bg-blue-50",    iconColor: "text-blue-600",   to: "/questions" },
    { label: "Aktif",        value: s.active,                icon: CheckCircle,   iconBg: "bg-green-50",   iconColor: "text-green-600",  to: "/questions?status=active" },
    { label: "Pasif",        value: s.passive,               icon: EyeOff,        iconBg: "bg-slate-100",  iconColor: "text-slate-500",  to: "/questions?status=passive" },
    { label: "Görselli",     value: s.with_image,            icon: Image,         iconBg: "bg-purple-50",  iconColor: "text-purple-600", to: "/questions?has_image=true" },
    { label: "Videolu",      value: s.with_video,            icon: Play,          iconBg: "bg-orange-50",  iconColor: "text-orange-500", to: "/questions?has_image=false&has_animation_video=true" },
    { label: "Eksik Verili", value: s.missing_data,          icon: AlertTriangle, iconBg: "bg-amber-50",   iconColor: "text-amber-500",  to: "/questions?missing_data=true&status=", alert: s.missing_data > 0 },
  ];

  return (
    <div className="p-8 fade-in space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Soru bankasına genel bakış</p>
        </div>
        <Link to="/questions/new" data-testid="new-question-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Plus size={15} /> Yeni Soru
        </Link>
      </div>

      {/* Bölüm 1 — Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-grid">
        {summaryCards.map((card) => <StatCard key={card.label} {...card} />)}
      </div>

      {/* Bölüm 2 — Uyarı Alanı */}
      {hasAlerts && (
        <div className="space-y-2" data-testid="alerts-section">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <AlertTriangle size={12} className="text-amber-500" /> Veri Kalitesi Uyarıları
          </p>
          <AlertBanner count={s.missing_correct_answer} label="boş doğru cevap (correct_answer)"
            to="/quality?tab=answer" />
          <AlertBanner count={s.missing_display_type} label="boş sunum tipi (display_type)"
            to="/quality?tab=type" />
        </div>
      )}

      {/* Bölüm 3 — Arşiv Dağılımı Grafiği */}
      {s.top_archives && s.top_archives.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="archive-chart">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen size={14} className="text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-800">Arşive Göre Soru Dağılımı</h2>
            </div>
            <Link to="/archives" className="text-xs text-blue-600 hover:text-blue-700 font-medium">Arşivleri Yönet →</Link>
          </div>
          <div className="p-4" style={{ height: 220, minHeight: 220, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={s.top_archives}
                margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                barCategoryGap="30%"
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={42}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                />
                <Tooltip
                  cursor={{ fill: "#f1f5f9" }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  formatter={(v) => [v + " soru", "Toplam"]}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {s.top_archives.map((_, i) => (
                    <Cell key={i} fill={`hsl(${215 + i * 18}, ${70 - i * 3}%, ${52 + i * 2}%)`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Bölüm 4+5 — Dağılım + Son Aktivite */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Arşiv dağılımı */}
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="archive-distribution">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen size={14} className="text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-800">Arşive Göre</h2>
            </div>
            <Link to="/archives" className="text-xs text-blue-600 hover:text-blue-700 font-medium">Yönet →</Link>
          </div>
          <div className="p-3 space-y-0.5">
            {(s.top_archives || []).map((a) => {
              const pct = s.total ? Math.round((a.count / s.total) * 100) : 0;
              return (
                <div key={a.name} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50">
                  <span className="text-xs text-slate-600 truncate flex-1">{a.name}</span>
                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 w-8 text-right">{a.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tip dağılımı */}
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="type-distribution">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <BookOpen size={14} className="text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-800">Tipe Göre</h2>
            </div>
          </div>
          <div className="p-3 space-y-0.5">
            {(s.type_distribution || []).map((t) => {
              const pct = s.total ? Math.round((t.count / s.total) * 100) : 0;
              return (
                <div key={t.type} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold border w-14 justify-center ${TYPE_COLORS[t.type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {t.type}
                  </span>
                  <span className="text-xs text-slate-500 flex-1 truncate">{TYPE_LABELS[t.type] || t.type}</span>
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 w-8 text-right">{t.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Son aktivite */}
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="recent-activity">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-800">Son Güncellenenler</h2>
            </div>
          </div>
          <div className="p-3 space-y-0.5">
            {(s.recent_activity || []).length === 0 ? (
              <p className="text-xs text-slate-400 px-2 py-3 text-center">Henüz aktivite yok</p>
            ) : (s.recent_activity || []).map((q) => {
              const updDate = q.updated_at ? q.updated_at.slice(0, 16).replace("T", " ") : "—";
              return (
                <Link key={q.id} to={`/questions/${q.id}`}
                  className="flex items-center gap-2 px-2 py-2 rounded hover:bg-slate-50 group">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-semibold border shrink-0 ${TYPE_COLORS[q.display_type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {q.display_type || "?"}
                  </span>
                  <span className="text-xs font-mono text-blue-600 group-hover:underline shrink-0">{q.question_code}</span>
                  <span className="text-xs text-slate-400 ml-auto shrink-0">{updDate.slice(0, 10)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bölüm 5 — Hızlı İşlemler */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Hızlı İşlemler</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
          {[
            { to: "/questions",         icon: List,     title: "Soru Listesi",   sub: "Tüm soruları yönet",      color: "text-blue-600",   bg: "bg-blue-50",   hover: "hover:border-blue-300 hover:bg-blue-50" },
            { to: "/import",            icon: Upload,   title: "Toplu Import",   sub: "Excel / CSV yükle",       color: "text-green-600",  bg: "bg-green-50",  hover: "hover:border-green-300 hover:bg-green-50" },
            { to: "/questions",         icon: Download, title: "Dışa Aktar",     sub: "Listeden Export et",      color: "text-purple-600", bg: "bg-purple-50", hover: "hover:border-purple-300 hover:bg-purple-50", state: { openExport: true } },
            { to: "/questions?missing_data=true&status=", icon: AlertTriangle, title: "Eksik Veri", sub: `${s.missing_data ?? 0} soru eksik`, color: "text-amber-600", bg: "bg-amber-50", hover: "hover:border-amber-300 hover:bg-amber-50" },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.title} to={item.to} state={item.state}
                className={`flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 transition-all group ${item.hover}`}
                data-testid={`quick-${item.title}`}>
                <div className={`w-9 h-9 ${item.bg} rounded-lg flex items-center justify-center shrink-0`}>
                  <Icon size={15} className={item.color} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-700 group-hover:text-slate-900">{item.title}</p>
                  <p className="text-xs text-slate-400 truncate">{item.sub}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
